/**
 * @file     ConfirmationStep.tsx
 * @readme   ../CLAUDE.md
 * @purpose  Post-submission confirmation screen shown after the lead capture form is submitted.
 */
'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Link from 'next/link';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { routes } from '@/core/constants';
import { useLandingAttribution } from '../landing/useLandingAttribution';

/** Set by LeadCaptureStep on a successful submit: '1' only when the SMS box was ticked with a phone. */
export const SMS_CONSENT_STORAGE_KEY = 'relta.dealFinderSmsConsent';

type ConfirmationStepProps = {
  onNavigate?: (path: string) => void;
  title?: string;
  body?: ReactNode;
  secondaryCtaLabel?: string;
};

export const ConfirmationStep = ({ onNavigate, title, body, secondaryCtaLabel }: ConfirmationStepProps) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const landing = useLandingAttribution();
  // The user is auto-signed-in for this session; their seeded filters + saved
  // search live on the dashboard, so that is always the highest-value next
  // step. Landing submissions carry welcome context so a user who clicks the
  // magic link later returns to the right view.
  const isLanding = Boolean(landing.slug);
  const dashboardHref = landing.slug
    ? `/dashboard?welcome=landing&slug=${encodeURIComponent(landing.slug)}${
        landing.variant ? `&variant=${encodeURIComponent(landing.variant)}` : ''
      }${landing.intent ? `&intent=${encodeURIComponent(landing.intent)}` : ''}`
    : '/dashboard/suggested-by-agent?welcome=deal-finder';

  // Read autoSignIn outcome stored by LeadCaptureStep, used to show a quick assurance
  // line when the account couldn't be silently signed in (e.g. magic link sent, race).
  const [visible, setVisible] = useState(false);
  const [signInStatus, setSignInStatus] = useState<string | null>(null);
  const [textConsented, setTextConsented] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  useEffect(() => {
    try {
      setSignInStatus(sessionStorage.getItem('relta.autoSignInStatus'));
      setTextConsented(sessionStorage.getItem(SMS_CONSENT_STORAGE_KEY) === '1');
    } catch {
      // ignore
    }
  }, []);
  const showEmailNotice =
    signInStatus === 'account_exists' ||
    signInStatus === 'account_created' ||
    signInStatus === 'error';

  const resolvedTitle = title ?? (isLanding
    ? t('dealFinder.confirmation.landingHeadline', {
        defaultValue: "You're in. Your dashboard is ready.",
      })
    : t('dealFinder.confirmation.headline', {
        defaultValue: 'Check your email to open your shortlist.',
      }));
  const textBody = isLanding
    ? t('dealFinder.confirmation.landingBody', {
        defaultValue:
          "You'll get a text with matches within one business day.",
      })
    : t('dealFinder.confirmation.body', {
        defaultValue:
          "You'll get a text within one business day to confirm your criteria. Reply STOP anytime.",
      });
  const noTextBody = isLanding
    ? t('dealFinder.confirmation.landingBodyNoText', {
        defaultValue: 'Your dashboard is live. Browse the MLS and save what you like.',
      })
    : t('dealFinder.confirmation.bodyNoText', {
        defaultValue: 'Expect a follow-up within one business day to confirm your criteria.',
      });
  const resolvedBody = body ?? (textConsented ? textBody : noTextBody);
  const resolvedCta = secondaryCtaLabel ?? (isLanding
    ? t('dealFinder.confirmation.landingSecondaryCta', { defaultValue: 'Read the latest market update' })
    : t('dealFinder.confirmation.secondaryCta', { defaultValue: 'Read the latest market update' }));
  // autoSignIn always fires on submit, so the dashboard is always the right
  // next step regardless of whether the lead came in via a landing page.
  const primaryCta = isLanding
    ? t('dealFinder.confirmation.landingPrimaryCta', { defaultValue: 'See your dashboard →' })
    : t('dealFinder.confirmation.primaryCta', { defaultValue: 'Open your dashboard →' });
  const primaryCtaHref = dashboardHref;

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 4, sm: 6 },
        borderRadius: `${theme.radius.lg}px`,
        backgroundColor: theme.colors.surface,
        opacity: visible ? 1 : 0,
        transition: 'opacity 350ms ease',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Typography sx={{ fontSize: 30, lineHeight: '38px', fontWeight: 600, color: theme.colors.textPrimary }}>
            {resolvedTitle}
          </Typography>
          <Typography sx={{ fontSize: theme.typography.bodyMd, lineHeight: '24px', color: theme.colors.textSecondary }}>
            {resolvedBody}
          </Typography>
          {showEmailNotice && (
            <Typography sx={{ fontSize: 13, color: theme.colors.textMuted ?? theme.colors.textSecondary, mt: 0.5 }}>
              {t('dealFinder.confirmation.emailNotice', {
                defaultValue: "Check your email. A link to sign in to your dashboard is on its way.",
              })}
            </Typography>
          )}
        </Box>

        {/* Primary CTA, academy onboarding handoff */}
        {onNavigate ? (
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => onNavigate(primaryCtaHref)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: `${theme.radius.md}px` }}
          >
            {primaryCta}
          </Button>
        ) : (
          <Button
            component={Link}
            href={primaryCtaHref}
            variant="contained"
            size="large"
            fullWidth
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: `${theme.radius.md}px` }}
          >
            {primaryCta}
          </Button>
        )}

        {onNavigate ? (
          <Button
            variant="text"
            size="large"
            fullWidth
            onClick={() => onNavigate(routes.marketUpdate)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {resolvedCta}
          </Button>
        ) : (
          <Button
            component={Link}
            href={routes.blog}
            variant="text"
            size="large"
            fullWidth
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {resolvedCta}
          </Button>
        )}
      </Box>
    </Paper>
  );
};
